/**
 * Mar Amoli — Favorites (wishlist) via localStorage
 *
 * Storage:
 *   - Key: "ma-favorites"
 *   - Value: JSON array of product handles, e.g. ["jabon-de-arroz", "jabon-confeti"]
 *
 * Triggers:
 *   - [data-ma-fav] buttons → toggle on click, set aria-pressed
 *   - [data-ma-fav-count] elements → display total count (header badge, etc.)
 *
 * Cross-tab sync via "storage" event.
 *
 * Note: localStorage es per-browser. No persiste entre dispositivos.
 * Para sincronizar a la cuenta del cliente (Shopify customer metafield),
 * tocaría agregar lógica de sync cuando customer.id != null. V2.
 */
(function () {
  "use strict";
  const STORAGE_KEY = "ma-favorites";

  function getFavs() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function setFavs(arr) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
    } catch (err) {
      console.warn("[ma-fav] localStorage write failed", err);
    }
    updateAllButtons();
    updateHeaderCount(arr.length);
  }

  function updateButton(btn) {
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    const isFav = getFavs().includes(handle);
    btn.setAttribute("aria-pressed", isFav ? "true" : "false");
    btn.setAttribute(
      "aria-label",
      isFav
        ? `Quitar ${btn.dataset.productTitle || "producto"} de favoritos`
        : `Guardar ${btn.dataset.productTitle || "producto"} en favoritos`,
    );
  }

  function updateAllButtons() {
    document.querySelectorAll("[data-ma-fav]").forEach(updateButton);
  }

  function updateHeaderCount(count) {
    document.querySelectorAll("[data-ma-fav-count]").forEach((el) => {
      el.textContent = String(count);
      el.dataset.maFavCount = String(count);
      el.classList.toggle("is-zero", count === 0);
    });
  }

  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-ma-fav]");
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    const handle = btn.dataset.productHandle;
    if (!handle) return;

    const favs = getFavs();
    const idx = favs.indexOf(handle);
    if (idx >= 0) {
      favs.splice(idx, 1);
    } else {
      favs.push(handle);
    }
    setFavs(favs);

    btn.classList.add("ma-pdp__fav--pulse", "ma-card__fav--pulse");
    setTimeout(() => {
      btn.classList.remove("ma-pdp__fav--pulse", "ma-card__fav--pulse");
    }, 400);

    document.dispatchEvent(
      new CustomEvent("ma:favorites:changed", {
        detail: { handles: getFavs() },
      }),
    );
  });

  // Cross-tab sync (storage event fires only in OTHER tabs)
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY) return;
    updateAllButtons();
    updateHeaderCount(getFavs().length);
  });

  // Public API for other components
  window.MaFavorites = {
    list: getFavs,
    has: (handle) => getFavs().includes(handle),
    add(handle) {
      const favs = getFavs();
      if (!favs.includes(handle)) {
        favs.push(handle);
        setFavs(favs);
      }
    },
    remove(handle) {
      const favs = getFavs();
      const idx = favs.indexOf(handle);
      if (idx >= 0) {
        favs.splice(idx, 1);
        setFavs(favs);
      }
    },
    clear() {
      setFavs([]);
    },
  };

  // ── Favoritos page renderer ─────────────────────────────────
  // Solo corre si la página tiene el container [data-ma-favs-container]
  // (sections/ma-favorites.liquid). Lee handles de localStorage, fetches
  // /products/<handle>.js por cada uno, renderiza una card.
  function moneyMxn(cents) {
    const value = cents / 100;
    const isInt = Math.abs(value - Math.round(value)) < 0.005;
    return value.toLocaleString("es-MX", {
      style: "currency",
      currency: "MXN",
      minimumFractionDigits: isInt ? 0 : 2,
      maximumFractionDigits: isInt ? 0 : 2,
    });
  }

  async function fetchProduct(handle) {
    try {
      const res = await fetch(`/products/${handle}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      console.warn(`[ma-fav] Could not fetch /products/${handle}.js`, err);
      return null;
    }
  }

  async function addToCart(variantId, btn) {
    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = "Agregando…";
    try {
      const formData = new FormData();
      formData.append("id", String(variantId));
      formData.append("quantity", "1");
      formData.append("sections", "cart-drawer");
      formData.append("sections_url", window.location.pathname);
      const res = await fetch("/cart/add.js", {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error("cart add failed");
      const result = await res.json();

      // Re-render drawer if available
      if (result.sections && result.sections["cart-drawer"]) {
        const drawer = document.querySelector("ma-cart-drawer");
        if (drawer) {
          const doc = new DOMParser().parseFromString(
            result.sections["cart-drawer"],
            "text/html",
          );
          const newDrawer = doc.querySelector("ma-cart-drawer");
          if (newDrawer) {
            drawer.replaceChildren(...Array.from(newDrawer.childNodes));
          }
        }
      }
      if (window.MaCart?.agregado) window.MaCart.agregado(); else if (window.MaCart?.open) window.MaCart.open();

      btn.textContent = "✓ Agregado";
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = original;
      }, 1100);
    } catch (err) {
      console.error("[ma-fav] add to cart failed", err);
      btn.textContent = "Reintentar";
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = original;
      }, 1500);
    }
  }

  async function renderFavoritesPage() {
    const container = document.querySelector("[data-ma-favs-container]");
    if (!container) return;

    const loadingEl = container.querySelector("[data-ma-favs-loading]");
    const emptyEl = container.querySelector("[data-ma-favs-empty]");
    const listEl = container.querySelector("[data-ma-favs-list]");
    const tpl = container.querySelector("[data-ma-favs-template]");
    if (!listEl || !tpl) return;

    const handles = getFavs();

    if (handles.length === 0) {
      loadingEl?.setAttribute("hidden", "");
      emptyEl?.removeAttribute("hidden");
      listEl.replaceChildren();
      return;
    }

    const products = await Promise.all(handles.map(fetchProduct));

    loadingEl?.setAttribute("hidden", "");
    listEl.replaceChildren();

    let renderedCount = 0;
    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      const handle = handles[i];
      const node = tpl.content.cloneNode(true);
      const item = node.querySelector(".ma-favs__item");
      const link = node.querySelector("[data-ma-favs-link]");
      const img = node.querySelector("[data-ma-favs-image]");
      const name = node.querySelector("[data-ma-favs-name]");
      const price = node.querySelector("[data-ma-favs-price]");
      const avail = node.querySelector("[data-ma-favs-avail]");
      const addBtn = node.querySelector("[data-ma-favs-add]");
      const remBtn = node.querySelector("[data-ma-favs-remove]");

      if (!product) {
        // Producto removido del store o handle inválido
        name.textContent = "Producto no disponible";
        link.removeAttribute("href");
        img.style.display = "none";
        price.textContent = "";
        avail.textContent = "Ya no está en la tienda";
        avail.classList.add("ma-favs__avail--out");
        addBtn.remove();
        remBtn.textContent = "Quitar de favoritos";
        remBtn.addEventListener("click", () => {
          window.MaFavorites.remove(handle);
          renderFavoritesPage();
        });
        item.classList.add("ma-favs__item--unavailable");
        listEl.appendChild(node);
        renderedCount++;
        continue;
      }

      const productUrl = product.url || `/products/${product.handle}`;
      const featuredImg = product.featured_image || (product.images && product.images[0]);
      link.href = productUrl;
      name.href = productUrl;
      name.textContent = product.title;

      if (featuredImg) {
        const src = featuredImg.replace(/(\.[a-z]+)(\?|$)/i, "_300x$1$2");
        img.src = src.startsWith("//") ? "https:" + src : src;
        img.alt = product.title;
      } else {
        img.style.display = "none";
      }

      const variant = product.variants && product.variants.find((v) => v.available);
      const firstVariant = product.variants && product.variants[0];
      const useVariant = variant || firstVariant;

      price.textContent = useVariant ? moneyMxn(useVariant.price) : "";
      if (useVariant && useVariant.available) {
        avail.textContent = "Disponible";
        addBtn.addEventListener("click", () => addToCart(useVariant.id, addBtn));
      } else {
        avail.textContent = "Agotado";
        avail.classList.add("ma-favs__avail--out");
        addBtn.disabled = true;
        addBtn.textContent = "Agotado";
      }

      remBtn.addEventListener("click", () => {
        window.MaFavorites.remove(handle);
        renderFavoritesPage();
      });

      listEl.appendChild(node);
      renderedCount++;
    }

    if (renderedCount === 0) {
      emptyEl?.removeAttribute("hidden");
    }
  }

  // Re-render when favorites change (e.g. user removes from another tab)
  document.addEventListener("ma:favorites:changed", renderFavoritesPage);

  // ── Customer metafield sync (read-only) ──────────────────────
  // Si el cliente tiene sesión Y el metafield custom.favorites tiene
  // handles, los unimos con localStorage. La escritura del metafield
  // requiere app + App Proxy (no se hace desde theme JS).
  function mergeCustomerFavorites() {
    if (!window.maCustomer) return;
    const remote = window.maCustomer.favorites;
    if (!Array.isArray(remote) || remote.length === 0) return;
    const local = getFavs();
    const merged = Array.from(new Set([...local, ...remote]));
    if (merged.length !== local.length) {
      setFavs(merged);
    }
  }

  // Initial sync
  function init() {
    mergeCustomerFavorites();
    updateAllButtons();
    updateHeaderCount(getFavs().length);
    renderFavoritesPage();
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
